import React from "react";
import Select from "src/components/Common/Select";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { AssignableUser, TicketData } from "./types";
import { RELATION_TYPE_OPTIONS, getRatingLabel } from "./utils";

const RatingStars = ({
  value,
  hoverValue,
  onChange,
  onHover,
  disabled,
}: {
  value: number;
  hoverValue: number;
  onChange: (nextValue: number) => void;
  onHover: (nextValue: number) => void;
  disabled?: boolean;
}) => {
  const activeValue = hoverValue || value;

  return (
    <div className="d-flex flex-wrap gap-2" onMouseLeave={() => onHover(0)} role="radiogroup" aria-label="Ticket rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <Button
          key={star}
          type="button"
          color={activeValue >= star ? "warning" : "light"}
          outline={activeValue < star}
          className="px-3"
          onMouseEnter={() => onHover(star)}
          onFocus={() => onHover(star)}
          onBlur={() => onHover(0)}
          onClick={() => onChange(star)}
          aria-label={`Rate ${star} out of 5`}
          aria-checked={value === star}
          role="radio"
          disabled={disabled}
        >
          ★
        </Button>
      ))}
    </div>
  );
};

type TicketRatingModalProps = {
  isOpen: boolean;
  ticketId?: number;
  ratingValue: number;
  ratingHover: number;
  ratingPreviewValue: number;
  ratingComment: string;
  submittingRating: boolean;
  onToggle: () => void;
  onChangeRating: (value: number) => void;
  onChangeHover: (value: number) => void;
  onChangeComment: (value: string) => void;
  onSubmit: () => void;
};

export const TicketRatingModal = ({
  isOpen,
  ticketId,
  ratingValue,
  ratingHover,
  ratingPreviewValue,
  ratingComment,
  submittingRating,
  onToggle,
  onChangeRating,
  onChangeHover,
  onChangeComment,
  onSubmit,
}: TicketRatingModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered>
    <ModalHeader toggle={onToggle}>
      Rate Ticket #{ticketId}
    </ModalHeader>
    <ModalBody>
      <div className="mb-3">
        <div className="text-muted text-uppercase fw-semibold small mb-1">Requester feedback</div>
        <h5 className="mb-2">How well was this ticket handled?</h5>
        <div className="text-muted">
          Capture the outcome while the resolution is still fresh. The score should reflect clarity, speed, and usefulness.
        </div>
      </div>

      <RatingStars
        value={ratingValue}
        hoverValue={ratingHover}
        onChange={onChangeRating}
        onHover={onChangeHover}
        disabled={submittingRating}
      />

      <div className="fw-semibold mt-3">
        {getRatingLabel(ratingPreviewValue)}
      </div>

      <div className="mt-3">
        <Label className="form-label">Comment</Label>
        <Input
          type="textarea"
          rows={4}
          value={ratingComment}
          onChange={(event) => onChangeComment(event.target.value)}
          placeholder="What worked well or where did the experience break down?"
          disabled={submittingRating}
        />
      </div>
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onToggle} disabled={submittingRating}>
        Cancel
      </Button>
      <Button color="warning" onClick={onSubmit} disabled={!ratingValue || submittingRating}>
        {submittingRating ? "Submitting..." : "Submit feedback"}
      </Button>
    </ModalFooter>
  </Modal>
);

type AddRelationModalProps = {
  isOpen: boolean;
  relationTicketId: string;
  relationType: string;
  addingRelation: boolean;
  onToggle: () => void;
  onChangeRelationTicketId: (value: string) => void;
  onChangeRelationType: (value: string) => void;
  onSubmit: () => void;
};

export const TicketAddRelationModal = ({
  isOpen,
  relationTicketId,
  relationType,
  addingRelation,
  onToggle,
  onChangeRelationTicketId,
  onChangeRelationType,
  onSubmit,
}: AddRelationModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered>
    <ModalHeader toggle={onToggle}>
      Link Related Ticket
    </ModalHeader>
    <ModalBody>
      <div className="mb-3">
        <Label>Ticket ID</Label>
        <Input
          type="number"
          value={relationTicketId}
          onChange={(event) => onChangeRelationTicketId(event.target.value)}
          placeholder="Enter ticket ID"
          disabled={addingRelation}
        />
      </div>
      <div>
        <Label>Relation type</Label>
        <Input
          type="select"
          value={relationType}
          onChange={(event) => onChangeRelationType(event.target.value)}
          disabled={addingRelation}
        >
          {RELATION_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      </div>
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onToggle} disabled={addingRelation}>
        Cancel
      </Button>
      <Button color="primary" onClick={onSubmit} disabled={!relationTicketId || addingRelation}>
        {addingRelation ? "Adding..." : "Add relation"}
      </Button>
    </ModalFooter>
  </Modal>
);

type TicketReassignModalProps = {
  isOpen: boolean;
  ticket: TicketData | null;
  reassignUserId: number | "";
  reassigningTicket: boolean;
  assignableUsers: AssignableUser[];
  onToggle: () => void;
  onChangeUserId: (value: number | "") => void;
  onSubmit: () => void;
};

export const TicketReassignModal = ({
  isOpen,
  ticket,
  reassignUserId,
  reassigningTicket,
  assignableUsers,
  onToggle,
  onChangeUserId,
  onSubmit,
}: TicketReassignModalProps) => {
  const assignableUserOptions = assignableUsers.map((user) => ({
    value: user.id,
    label: `${user.name}${user.id === ticket?.assignee?.id ? " (current)" : ""}`,
  }));

  return (
    <Modal isOpen={isOpen} toggle={onToggle} centered>
      <ModalHeader toggle={onToggle}>
        Reassign Ticket
      </ModalHeader>
      <ModalBody>
        <Label>Select new assignee</Label>
        <Select
          isSearchable
          name="assignee_id"
          label="Assignee"
          placeholder="Select user"
          isDisabled={reassigningTicket}
          options={assignableUserOptions}
          onChange={(value: any) => onChangeUserId(value?.value ? Number(value.value) : "")}
          value={assignableUserOptions.find((item) => item.value === reassignUserId) || null}
        />
        {assignableUsers.length === 0 && (
          <small className="text-muted d-block mt-2">
            No available users found for reassignment.
          </small>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onToggle} disabled={reassigningTicket}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={onSubmit}
          disabled={!reassignUserId || reassigningTicket || Number(reassignUserId) === ticket?.assignee?.id}
        >
          {reassigningTicket ? "Reassigning..." : "Reassign"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

type TicketDeleteModalProps = {
  isOpen: boolean;
  ticket: TicketData | null;
  deleteExpectedPhrase: string;
  deleteConfirmation: string;
  deletingTicket: boolean;
  canConfirmDelete: boolean;
  onToggle: () => void;
  onChangeConfirmation: (value: string) => void;
  onSubmit: () => void;
};

export const TicketDeleteModal = ({
  isOpen,
  ticket,
  deleteExpectedPhrase,
  deleteConfirmation,
  deletingTicket,
  canConfirmDelete,
  onToggle,
  onChangeConfirmation,
  onSubmit,
}: TicketDeleteModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered>
    <ModalHeader toggle={onToggle}>
      {ticket ? `Delete Ticket #${ticket.id}` : "Delete Ticket"}
    </ModalHeader>
    <ModalBody>
      <p className="mb-2">This action is irreversible.</p>
      <p className="mb-3">
        Type <code>{deleteExpectedPhrase}</code> to confirm deletion.
      </p>
      <Input
        value={deleteConfirmation}
        onChange={(event) => onChangeConfirmation(event.target.value)}
        placeholder={deleteExpectedPhrase}
        disabled={deletingTicket}
      />
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onToggle} disabled={deletingTicket}>
        Cancel
      </Button>
      <Button color="danger" onClick={onSubmit} disabled={!canConfirmDelete}>
        {deletingTicket ? "Deleting..." : "Delete ticket"}
      </Button>
    </ModalFooter>
  </Modal>
);

type TicketCannedResponseDeleteModalProps = {
  isOpen: boolean;
  responseTitle: string;
  deletingResponse: boolean;
  onToggle: () => void;
  onSubmit: () => void;
};

export const TicketCannedResponseDeleteModal = ({
  isOpen,
  responseTitle,
  deletingResponse,
  onToggle,
  onSubmit,
}: TicketCannedResponseDeleteModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered>
    <ModalHeader toggle={onToggle}>
      Delete Template
    </ModalHeader>
    <ModalBody>
      <p className="mb-2">This action is irreversible.</p>
      <p className="mb-0">
        Delete template <span className="fw-semibold">"{responseTitle}"</span>?
      </p>
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onToggle} disabled={deletingResponse}>
        Cancel
      </Button>
      <Button color="danger" onClick={onSubmit} disabled={deletingResponse}>
        {deletingResponse ? "Deleting..." : "Delete template"}
      </Button>
    </ModalFooter>
  </Modal>
);

type TicketImagePreviewModalProps = {
  isOpen: boolean;
  imagePreview: { name: string; url: string } | null;
  onToggle: () => void;
};

export const TicketImagePreviewModal = ({
  isOpen,
  imagePreview,
  onToggle,
}: TicketImagePreviewModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered size="lg">
    <ModalHeader toggle={onToggle}>
      {imagePreview?.name || "Image preview"}
    </ModalHeader>
    <ModalBody className="text-center">
      {imagePreview?.url && (
        <img
          src={imagePreview.url}
          alt={imagePreview.name}
          style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
        />
      )}
    </ModalBody>
  </Modal>
);

type TicketCannedResponseModalProps = {
  isOpen: boolean;
  isEditing: boolean;
  titleValue: string;
  bodyValue: string;
  scopeValue: "all" | "category";
  canUseCurrentCategory: boolean;
  currentCategoryLabel: string;
  isShared: boolean;
  canManageSharedResponses: boolean;
  savingResponse: boolean;
  onToggle: () => void;
  onChangeTitle: (value: string) => void;
  onChangeBody: (value: string) => void;
  onChangeScope: (value: "all" | "category") => void;
  onChangeShared: (value: boolean) => void;
  onSubmit: () => void;
};

export const TicketCannedResponseModal = ({
  isOpen,
  isEditing,
  titleValue,
  bodyValue,
  scopeValue,
  canUseCurrentCategory,
  currentCategoryLabel,
  isShared,
  canManageSharedResponses,
  savingResponse,
  onToggle,
  onChangeTitle,
  onChangeBody,
  onChangeScope,
  onChangeShared,
  onSubmit,
}: TicketCannedResponseModalProps) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered>
    <ModalHeader toggle={onToggle}>
      {isEditing ? "Edit Template" : "New Template"}
    </ModalHeader>
    <ModalBody>
      <div className="mb-3">
        <Label className="form-label">Title</Label>
        <Input
          value={titleValue}
          onChange={(event) => onChangeTitle(event.target.value)}
          placeholder="Short template title"
          maxLength={255}
          disabled={savingResponse}
        />
      </div>
      <div className="mb-3">
        <Label className="form-label">Message</Label>
        <Input
          type="textarea"
          rows={6}
          value={bodyValue}
          onChange={(event) => onChangeBody(event.target.value)}
          placeholder="Template text"
          maxLength={10000}
          disabled={savingResponse}
        />
      </div>
      <div className="mb-3">
        <Label className="form-label">Availability</Label>
        <Input
          type="select"
          value={scopeValue}
          onChange={(event) => onChangeScope(event.target.value === "category" ? "category" : "all")}
          disabled={savingResponse}
        >
          <option value="all">All categories</option>
          {canUseCurrentCategory && (
            <option value="category">{currentCategoryLabel}</option>
          )}
        </Input>
      </div>
      {canManageSharedResponses && (
        <div>
          <Label className="form-label">Template access</Label>
          <Input
            type="select"
            value={isShared ? "shared" : "personal"}
            onChange={(event) => onChangeShared(event.target.value === "shared")}
            disabled={savingResponse}
          >
            <option value="personal">Personal only</option>
            <option value="shared">Shared</option>
          </Input>
          <div>
            <small className="text-muted">
              Shared templates are available to all users who can reply in tickets.
            </small>
          </div>
        </div>
      )}
    </ModalBody>
    <ModalFooter>
      <Button color="light" onClick={onToggle} disabled={savingResponse}>
        Cancel
      </Button>
      <Button
        color="primary"
        onClick={onSubmit}
        disabled={!titleValue.trim() || !bodyValue.trim() || savingResponse}
      >
        {savingResponse ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save changes" : "Create template")}
      </Button>
    </ModalFooter>
  </Modal>
);
